import { CloseOutlined, MenuOutlined } from '@ant-design/icons';
import React from 'react';
import { FmButton } from './FmButton';
import { FmSelect } from './FmSelect';
import type { FmProductChromeDescriptor } from './FmProductPage.types';
import { AuthModal } from '@/components/auth/AuthModal';
import {
  FmTopBarDrawerItem,
  FmTopBarHamburger,
  FmTopBarLanguageWrap,
  FmTopBarNavDrawer,
  FmTopBarNavTab,
  FmTopBarShellHeader,
  FmTopBarWordmark,
} from '@/shared/ui/FmTopBarShell';

export interface FmProductChromeProps {
  descriptor: FmProductChromeDescriptor;
}

function scrollToChapter(chapterId: string): void {
  document.getElementById(chapterId)?.scrollIntoView({
    behavior: 'auto',
    block: 'start',
  });
}

const IdentityActions: React.FC<{
  identity: FmProductChromeDescriptor['identity'];
}> = ({ identity }) => {
  if (identity.status === 'signed-in') {
    return (
      <span className="fm-product-chrome__identity">
        <FmButton onClick={identity.onDashboard} data-testid="product-chrome-account">
          {identity.accountLabel}
        </FmButton>
        <FmButton variant="text" onClick={identity.onLogout} data-testid="product-chrome-logout">
          {identity.logoutLabel}
        </FmButton>
      </span>
    );
  }
  return (
    <span className="fm-product-chrome__identity">
      <FmButton variant="bordered" onClick={identity.onLogin} data-testid="landing-auth-login">
        {identity.loginLabel}
      </FmButton>
      <FmButton variant="primary" onClick={identity.onRegister} data-testid="landing-auth-register">
        {identity.registerLabel}
      </FmButton>
    </span>
  );
};

export const FmProductChrome: React.FC<FmProductChromeProps> = ({ descriptor }) => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const runAndClose = (action: () => void) => (): void => {
    action();
    setDrawerOpen(false);
  };
  return (
    <FmTopBarShellHeader
      testId="product-chrome"
      navAriaLabel={descriptor.navigationAriaLabel}
      left={
        <>
          <FmTopBarWordmark testId="product-chrome-wordmark" onActivate={descriptor.onHome}>
            {descriptor.brandLabel}
          </FmTopBarWordmark>
          <FmTopBarHamburger
            testId="product-chrome-hamburger"
            open={drawerOpen}
            ariaLabel={drawerOpen ? descriptor.closeMenuLabel : descriptor.openMenuLabel}
            onToggle={() => setDrawerOpen((open) => !open)}
          >
            {drawerOpen ? <CloseOutlined /> : <MenuOutlined />}
          </FmTopBarHamburger>
        </>
      }
      nav={descriptor.navigation.map((item) => (
        <FmTopBarNavTab
          key={item.id}
          testId={`product-chrome-nav-${item.id}`}
          onClick={() => scrollToChapter(item.chapterId)}
        >
          {item.label}
        </FmTopBarNavTab>
      ))}
      right={
        <>
          <FmTopBarLanguageWrap>
            <FmSelect
              value={descriptor.language.value}
              onChange={(value) => descriptor.language.onChange(String(value))}
              options={descriptor.language.options.map((option) => ({ ...option }))}
              data-testid="landing-language-select"
              ariaLabel={descriptor.language.ariaLabel}
              controlWidth="sm"
            />
          </FmTopBarLanguageWrap>
          <IdentityActions identity={descriptor.identity} />
        </>
      }
    >
      {drawerOpen ? (
        <FmTopBarNavDrawer
          backdropTestId="product-chrome-drawer-backdrop"
          drawerTestId="product-chrome-drawer"
          onClose={() => setDrawerOpen(false)}
          ariaLabel={descriptor.menuAriaLabel}
        >
          {descriptor.navigation.map((item) => (
            <FmTopBarDrawerItem
              key={item.id}
              onClick={runAndClose(() => scrollToChapter(item.chapterId))}
            >
              {item.label}
            </FmTopBarDrawerItem>
          ))}
          {descriptor.identity.status === 'signed-out' ? (
            <>
              <FmTopBarDrawerItem onClick={runAndClose(descriptor.identity.onLogin)}>
                {descriptor.identity.loginLabel}
              </FmTopBarDrawerItem>
              <FmTopBarDrawerItem onClick={runAndClose(descriptor.identity.onRegister)}>
                {descriptor.identity.registerLabel}
              </FmTopBarDrawerItem>
            </>
          ) : (
            <>
              <FmTopBarDrawerItem onClick={runAndClose(descriptor.identity.onDashboard)}>
                {descriptor.identity.dashboardLabel}
              </FmTopBarDrawerItem>
              <FmTopBarDrawerItem onClick={runAndClose(descriptor.identity.onLogout)}>
                {descriptor.identity.logoutLabel}
              </FmTopBarDrawerItem>
            </>
          )}
        </FmTopBarNavDrawer>
      ) : null}
      <AuthModal
        open={descriptor.authDialog.open}
        onCancel={descriptor.authDialog.onClose}
        defaultMode={descriptor.authDialog.mode}
        onAuthenticated={descriptor.authDialog.onAuthenticated}
        title={descriptor.authDialog.title}
        context={descriptor.authDialog.context}
        closeLabel={descriptor.authDialog.closeLabel}
      />
    </FmTopBarShellHeader>
  );
};

FmProductChrome.displayName = 'FmProductChrome';

export default FmProductChrome;
